import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import "rc-slider/assets/index.css";
import "./styles/index.scss";
import "./index.css";
import "./fonts/line-awesome-1.3.0/css/line-awesome.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Toaster, toast, useToasterStore } from "react-hot-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { DataProvider } from "data/data-provider";
import { globalJson } from "global/global_json";
import serverURL from "api/auth/auth";
import { setStripeAvailability } from "utils/stripeAvailability";
import { setPostHogCompany } from "analytics/posthog";

// Scope all PostHog events to this tenant (this site's company id is static,
// unlike Template's runtime-resolved tenant) before the first $pageview can fire.
setPostHogCompany(globalJson?.prod_company_id);

const isPrerendering =
  typeof navigator !== "undefined" &&
  (navigator.userAgent.includes("jsdom") ||
    (window.navigator && window.navigator.userAgent.includes("jsdom")));

// Improved check for first render and hydration
const isHydrating = () => {
  if (typeof window === 'undefined') return true;
  return document.readyState !== 'complete' || 
         typeof window.__REACT_QUERY_HYDRATION__ !== 'undefined' ||
         window.__INIT_COMPLETED__ !== true;
};

// Global flag to track initial render completion
if (typeof window !== 'undefined') {
  // Initialize the property to avoid undefined access
  window.__INIT_COMPLETED__ = false;
  
  // Set the flag after a short delay to ensure hydration is complete
  setTimeout(() => {
    window.__INIT_COMPLETED__ = true;
  }, 1000);
}

async function fetchStripeKey(
  companyId: number
): Promise<{ publishableKey: string; stripeAccount?: string } | null> {
  // Enhanced check to prevent API calls during any initialization phase
  if (isPrerendering || document.readyState !== 'complete' ||
      typeof window === 'undefined' || window.__INIT_COMPLETED__ !== true) {
    return null;
  }

  try {
    console.log("Fetching Stripe key for companyId:", companyId);

    const response = await fetch(
      `${serverURL}/api/payment/publishable-key/?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("API response:", response);

    const contentType = response.headers.get("content-type");
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch Stripe key. Status:", response.status);
      console.error("Response Error Body:", errorText);
      return null;
    }

    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();
      console.log("Full API Response JSON:", data);

      if (data.publishable_key) {
        console.log("Fetched Stripe key:", data.publishable_key);
        // v2 (API-created) hosts return the PLATFORM publishable key plus the
        // connected account id; Stripe.js must be initialised with
        // `stripeAccount` so PaymentIntents created on that account can be
        // confirmed. Legacy OAuth hosts omit stripe_account. (1NOW-1234)
        return {
          publishableKey: data.publishable_key,
          stripeAccount: data.stripe_account || undefined,
        };
      } else {
        console.error("publishable_key is undefined in the API response.");
        return null;
      }
    } else {
      const errorText = await response.text();
      console.error("Unexpected content type:", contentType);
      console.error("Response Error Body:", errorText);
      return null;
    }
  } catch (error) {
    console.error("Error fetching Stripe key:", error);
    return null;
  }
}

/**
 * One failure on screen at a time.
 *
 * A single renter action fans out into several requests — a search hits
 * locations, availability and pricing together — so one dropped connection
 * stacked three identical toasts down the corner of the page. That reads as
 * three separate things having gone wrong, and buries whatever the renter was
 * looking at underneath them.
 *
 * Giving every error the same toast id makes react-hot-toast update the
 * existing one in place instead of mounting another, so the slot is reused
 * with no stacking and no cross-fade between two boxes. Done here, once, on
 * the shared `toast` singleton rather than at every call site; a caller that
 * passes its own id still wins, which is why the store-based cap below stays
 * as a backstop.
 */
const ERROR_TOAST_ID = "renter-error";
const showError = toast.error;
toast.error = ((message: any, options?: any) =>
  showError(message, {
    id: ERROR_TOAST_ID,
    // Set per-toast, not on <Toaster toastOptions>. react-hot-toast does not
    // merge ariaProps from there, so configuring it on the Toaster silently
    // left every error on the library default of role="status" /
    // aria-live="polite" — failures should interrupt, not wait for an idle
    // moment (ONENOW-465).
    ariaProps: { role: "alert", "aria-live": "assertive" as const },
    ...options,
  })) as typeof toast.error;

/**
 * Backstop for error toasts raised with an explicit id of their own, which
 * bypass the shared slot above. Dismissing the surplus from the store is
 * react-hot-toast's own documented pattern for capping concurrent toasts; the
 * store keeps the newest at index 0, so this keeps the error matching
 * whatever the renter just did. Success and loading toasts are untouched.
 */
const SingleErrorToast = () => {
  const { toasts } = useToasterStore();

  useEffect(() => {
    toasts
      .filter((t) => t.visible && t.type === "error")
      .slice(1)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  return null;
};

/** Long Stripe/API error strings used to overflow the toast box (ONENOW-465). */
const errorToastOptions = {
  style: {
    maxWidth: "min(420px, calc(100vw - 32px))",
    wordBreak: "break-word" as const,
    overflowWrap: "anywhere" as const,
    whiteSpace: "pre-wrap" as const,
  },
  error: {
    duration: 6000,
  },
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const RootComponent = () => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(!isPrerendering);
  const [retryCount, setRetryCount] = useState(0);
  const [isFullyInitialized, setIsFullyInitialized] = useState(false);
  const maxRetries = 3;
  const hasMountedRef = useRef(false);

  // Set up a timer to mark the component as fully mounted
  useEffect(() => {
    if (isPrerendering) return;
    
    const timer = setTimeout(() => {
      hasMountedRef.current = true;
      setIsFullyInitialized(true);
    }, 1500); // Wait 1.5s after mount before considering fully initialized
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isPrerendering) {
      setLoading(false);
      return;
    }

    // Don't try to initialize until the DOM is fully loaded
    if (document.readyState !== 'complete') {
      const handleLoad = () => {
        // Delay to ensure React is done with initial rendering
        setTimeout(() => setRetryCount(c => c + 1), 500);
      };
      
      window.addEventListener('load', handleLoad, { once: true });
      return () => window.removeEventListener('load', handleLoad);
    }

    // Delay initialization to ensure the app is properly mounted
    const delay = retryCount > 0 ? 1000 * retryCount : 300;
    
    const initializeStripe = async () => {
      const companyId = globalJson?.prod_company_id;

      if (!companyId) {
        console.error("Company ID is not available.");
        setStripePromise(null);
        setStripeAvailability(Promise.resolve(false));
        setLoading(false);
        return;
      }

      try {
        // Additional safety check - don't make API calls until fully initialized
        if (!hasMountedRef.current || !isFullyInitialized) {
          setLoading(false);
          return;
        }

        const stripeKey = await fetchStripeKey(companyId);
        if (stripeKey) {
          // For v2 hosts, init Stripe.js bound to the connected account so the
          // platform key can confirm PaymentIntents created on it. (1NOW-1234)
          //
          // Swallow the rejection here rather than handing a rejected promise
          // to `<Elements>` (which has no catch of its own) -- Stripe.js
          // throws for a key it won't accept, and without this the card
          // fields just render empty and unclickable with nothing explaining
          // why. (1NOW-1608)
          const stripeLoad = loadStripe(
            stripeKey.publishableKey,
            stripeKey.stripeAccount
              ? { stripeAccount: stripeKey.stripeAccount }
              : undefined
          ).catch(error => {
            console.error("Stripe.js failed to initialize:", error);
            return null;
          });
          setStripePromise(stripeLoad);
          setStripeAvailability(stripeLoad.then(stripe => !!stripe));
        } else if (retryCount < maxRetries) {
          // Retry logic
          setRetryCount(prevCount => prevCount + 1);
          return; // Will trigger re-render and this effect will run again
        } else {
          console.warn("Failed to load Stripe key after retries");
          setStripePromise(null);
          setStripeAvailability(Promise.resolve(false));

          // Only show error if we're fully initialized
          if (isFullyInitialized && hasMountedRef.current && document.readyState === 'complete') {
            // Delay the toast to ensure UI is stable
            setTimeout(() => {
              toast.error(
                "Error loading Stripe. Some payment features may be unavailable.",
                { duration: 4000, id: 'stripe-error' }
              );
            }, 500);
          }
        }
      } catch (error) {
        console.error("Error during Stripe initialization:", error);
        setStripePromise(null);
        setStripeAvailability(Promise.resolve(false));
      } finally {
        setLoading(false);
      }
    };

    // Delay initialization if we're retrying
    const timeoutId = setTimeout(initializeStripe, delay);
    return () => clearTimeout(timeoutId);
  }, [retryCount, isFullyInitialized]);

  if (loading) {
    return (
      <div className="flex justify-center items-center col-span-full min-h-[100vh]">
        <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-700"></div>
      </div>
    );
  }

  if (isPrerendering) {
    return (
      <GoogleOAuthProvider clientId="792199944204-3u7t512ta8hbrhptdjomhqp2nb7jh41h.apps.googleusercontent.com">
        <DataProvider>
          <App />
          <SingleErrorToast />
          <Toaster toastOptions={errorToastOptions} />
        </DataProvider>
      </GoogleOAuthProvider>
    );
  }

  // Always provide an <Elements> context at runtime — even when Stripe failed
  // to load (stripePromise === null). `<Elements stripe={null}>` is valid and
  // makes useStripe()/useElements() return null instead of throwing
  // "Could not find Elements context", so CheckOutPage keeps rendering and the
  // Square path still works.
  return (
    <GoogleOAuthProvider clientId="792199944204-3u7t512ta8hbrhptdjomhqp2nb7jh41h.apps.googleusercontent.com">
      <DataProvider>
        <Elements stripe={stripePromise}>
          <App />
        </Elements>
        <SingleErrorToast />
        <Toaster toastOptions={errorToastOptions} />
      </DataProvider>
    </GoogleOAuthProvider>
  );
};

// For react-snap compatibility
// This helps React-Snap know when hydration is complete
if (typeof window !== "undefined") {
  // Original snapSaveState functionality
  const originalSnapSaveState = window.snapSaveState;
  
  // Override the function to also set our init flag
  window.snapSaveState = function() {
    window.__INIT_COMPLETED__ = true;
    // Call original if it exists
    if (originalSnapSaveState) {
      originalSnapSaveState.call(window);
    }
  };
}

root.render(
  <RootComponent />
);

// Delay the reportWebVitals to ensure the app has time to fully initialize
setTimeout(() => {
  reportWebVitals();
}, 2000);
